const INSTALLATION_ID_KEY = 'lilius.installationId';
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

type InstallationStorage = Pick<Storage, 'getItem' | 'setItem'>;

let volatileInstallationId: string | null = null;
let persistenceUnavailable = false;

function createUuid(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (typeof globalThis.crypto?.getRandomValues === 'function') {
    globalThis.crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0'));
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10).join('')}`;
}

function browserStorage(): InstallationStorage | null {
  try {
    return typeof globalThis.localStorage === 'undefined' ? null : globalThis.localStorage;
  } catch {
    return null;
  }
}

export function getInstallationId(
  storage: InstallationStorage | null = browserStorage(),
  generate: () => string = createUuid,
): string {
  if (storage) {
    try {
      const stored = storage.getItem(INSTALLATION_ID_KEY);
      if (stored && UUID_PATTERN.test(stored)) {
        volatileInstallationId = stored;
        persistenceUnavailable = false;
        return stored;
      }
      if (persistenceUnavailable && volatileInstallationId) return volatileInstallationId;
      const installationId = generate();
      volatileInstallationId = installationId;
      try {
        storage.setItem(INSTALLATION_ID_KEY, installationId);
        persistenceUnavailable = false;
      } catch {
        persistenceUnavailable = true;
        // The same identifier remains stable for this page lifetime.
      }
      return installationId;
    } catch {
      persistenceUnavailable = true;
      // Fall through to the in-memory identifier.
    }
  }

  const installationId = volatileInstallationId ?? generate();
  volatileInstallationId = installationId;
  return installationId;
}
